import { api } from '@/lib/api';

// ─── Types ──────────────────────────────────────────

export interface ChatMessage {
  role: 'assistant' | 'user';
  content: string;
}

export interface SuggestedDatabase {
  name: string;
  type: string;
  reason: string;
  existingDatabaseId?: string;
}

export interface MigrationBlockRef {
  id: string;
  topic: string;
  action: string;
  promptExcerpt: string;
  reason: string;
}

export interface CapabilityConfigs {
  calendar?: { allowedUserIds?: string[] };
  lead_management?: { funnelId?: string };
}

export interface AgentDraft {
  name: string;
  description: string;
  systemPrompt: string;
  category: string;
  segment: string;
  model: string;
  temperature: number;
  enabledCapabilities: string[];
  suggestedDatabases: SuggestedDatabase[];
  migrationBlocks: MigrationBlockRef[];
  databaseIds?: string[];
  capabilityConfigs?: CapabilityConfigs;
}

export interface MultiChoiceOption {
  value: string;
  label: string;
  description?: string;
}

export type MessageComponent =
  | { type: 'user_picker'; data: { users: { id: string; name: string; avatarUrl?: string }[] } }
  | { type: 'funnel_confirm'; data: { name: string; stages: string[] } }
  | { type: 'database_picker'; data: { databases: { id: string; name: string }[]; multi?: boolean } }
  | { type: 'funnel_picker'; data: { funnels: { id: string; name: string }[]; allowCreate: boolean } }
  | {
      type: 'multi_choice';
      data: {
        question: string;
        mode: 'single' | 'multi';
        options: MultiChoiceOption[];
        submitLabel?: string;
      };
    };

export interface ActionIntent {
  type:
    | 'set_capability'
    | 'link_database'
    | 'link_databases'
    | 'create_database'
    | 'create_databases'
    | 'create_funnel'
    | 'set_funnel'
    | 'set_calendar_users'
    | 'submit_multi_choice';
  payload?: Record<string, unknown>;
}

export interface ChatDraftRequest {
  messages: ChatMessage[];
  currentDraft: AgentDraft;
  pendingIntent?: ActionIntent;
}

export interface ChatDraftResponse {
  message: string;
  draft: AgentDraft;
  messageComponent?: MessageComponent;
}

export interface GenerateDraftResponse {
  draft: AgentDraft;
  assistantMessage: string;
}

export interface RefineDraftResponse {
  draft: AgentDraft;
  assistantMessage: string;
  migrationBlocks: MigrationBlockRef[];
}

// ─── API ────────────────────────────────────────────

export const agentDraftApi = {
  async generateDraft(
    messages: ChatMessage[],
    existingDatabases: string[],
  ): Promise<GenerateDraftResponse> {
    const response = await api.post('ai-agents/generate-draft', {
      messages,
      existingDatabases,
    });
    return response.data.data;
  },

  async refineDraft(
    currentDraft: AgentDraft,
    userMessage: string,
    chatHistory: ChatMessage[],
  ): Promise<RefineDraftResponse> {
    const response = await api.post('ai-agents/generate-draft/refine', {
      currentDraft,
      userMessage,
      chatHistory,
    });
    return response.data.data;
  },

  async chat(request: ChatDraftRequest): Promise<ChatDraftResponse> {
    const response = await api.post('ai-agents/generate-draft/chat', request);
    return response.data.data;
  },

  async finalizeDraft(
    messages: ChatMessage[],
    currentDraft: AgentDraft,
  ): Promise<{ systemPrompt: string }> {
    const response = await api.post('ai-agents/generate-draft/finalize', {
      messages,
      currentDraft,
    });
    return response.data.data;
  },
};
